/**
 * Decoupling (part-share transfer) maths.
 *
 * One co-owner — "A", the exiting owner — transfers their share of a jointly held
 * property to the other, "B", the remaining owner, who ends up holding 100%. A's name
 * comes off the title, so A's next purchase is assessed as a *first* property (0% ABSD
 * for a Singapore Citizen rather than 20%).
 *
 * The transaction is a sale and a purchase at once: BSD/ABSD fall on B as the acquirer,
 * SSD falls on A as the disposer, and both are charged on the market value of the share
 * that moves — not on the whole property.
 *
 * Every function here is pure. The React page under
 * pages/calculators/DecouplingCalculator.tsx owns all the state and formatting.
 *
 * The loan formulas below are a deliberate local copy of the ones inside
 * BuyingCalculator.tsx (see its "Calculation Helpers" block) rather than a shared import:
 * the Buying Calculator is a 10k-line monolith and extracting from it was ruled out of
 * scope. Stamp duty, by contrast, IS shared — see the imports.
 */

import {
  calculateBsd,
  getAbsdRate,
  type CitizenshipType,
  type PropertyCount,
} from '../../utils/stampDutyRates';
import { calculateSellerStampDuty, type SellerStampDutyResult } from './sellerStampDuty';

// ─── Regulatory constants ─────────────────────────────────────────────────────

/** TDSR caps total monthly debt at 55% of assessable income. MAS Notice 645. */
export const TDSR_RATIO = 0.55;

/**
 * MSR caps the monthly *property* loan repayment at 30% of gross monthly income, and
 * applies only to HDB flats and ECs bought from a developer. Where it applies, the loan
 * is capped by the LOWER of MSR and TDSR.
 * https://www.mas.gov.sg/regulation/explainers/new-housing-loans/msr-and-tdsr-rules
 */
export const MSR_RATIO = 0.30;

/** Standard LTV for a borrower with no other outstanding housing loan. */
export const LTV_STANDARD = 0.75;
/** LTV once the tenure runs past the standard cap (over 30 years, or past age 65). */
export const LTV_EXTENDED = 0.55;
/** LTV when the borrower already carries another housing loan. */
export const LTV_SECOND_LOAN = 0.45;

/** Standard tenure: min(30 yrs, 65 − age). Beyond this the LTV drops to 55%. */
export const TENURE_CAP_STANDARD_YEARS = 30;
export const TENURE_AGE_CEILING_STANDARD = 65;
/** Extended tenure: min(35 yrs, 75 − age), at the cost of a 55% LTV. */
export const TENURE_CAP_EXTENDED_YEARS = 35;
export const TENURE_AGE_CEILING_EXTENDED = 75;

/**
 * IRAS audits 99-to-1 style arrangements under s33A of the Stamp Duties Act as ABSD
 * avoidance. A share at or below this size is the classic pattern, so the UI warns.
 */
export const NINETY_NINE_TO_ONE_SHARE_PCT = 5;

/**
 * HDB decoupling has been barred for married couples since this date, bar a short list
 * of exceptions. https://www.hdb.gov.sg
 */
export const HDB_DECOUPLING_BAN_DATE_LABEL = '1 Apr 2016';

/** An EC only leaves HDB rules — and so only becomes decouplable — after its MOP. */
export const EC_MOP_YEARS = 5;

// ─── Types ────────────────────────────────────────────────────────────────────

export type DecouplingPropertyTab = 'private' | 'ec' | 'hdb';

/** One side of the transfer. */
export interface DecouplingOwner {
  name?: string;
  /** Percentage of the property this owner holds before the transfer. A + B total 100. */
  sharePct: number;
  /** CPF drawn for this property plus accrued interest — refunded to this owner's OA. */
  cpfWithdrawal: number;
  citizenship: CitizenshipType;
  /**
   * Properties this owner holds *including* the one being decoupled. Drives the ABSD
   * rate on the acquisition. '1st' is the usual case for B: they already part-own this
   * property and own nothing else, so ABSD is 0% for an SC.
   */
  propertyCount: PropertyCount;
}

export interface DecouplingProperty {
  /** Current market value of the whole property — the basis for the share value. */
  marketValue: number;
  /** Outstanding balance on the existing joint loan, redeemed in full on completion. */
  outstandingLoan: number;
  /** Whether Seller's Stamp Duty is charged on this transfer. Off until the agent
   *  switches it on and supplies a purchase date, so a blank form never shows an SSD
   *  figure derived from a date nobody entered. */
  ssdApplies: boolean;
  /** ISO yyyy-mm-dd. For an EC this is key collection, NOT the end of MOP. */
  purchaseDate: string;
  /** ISO yyyy-mm-dd — completion of the transfer. */
  transferDate: string;
}

/** Flat costs of the transfer. See FEE_GUIDANCE for the typical bands. */
export interface DecouplingFees {
  /** B's conveyancing. */
  buyerLegalFee: number;
  /** A's conveyancing — the two sides must use different firms. */
  sellerLegalFee: number;
  /** Early-redemption penalty on the existing loan, if still inside its lock-in. */
  bankLoanPenalty: number;
}

export interface DecouplingFinancingInputs {
  /** Remaining owner B's age at the transfer — drives the tenure cap. */
  age: number;
  monthlyIncome: number;
  otherAnnualIncome: number;
  isSelfEmployed: boolean;
  existingMonthlyDebt: number;
  /** Cash B puts in, reducing the new loan. */
  cashAvailable: number;
  /** CPF OA B puts in, reducing the new loan. */
  cpfAvailable: number;
  /** Requested tenure in years; clamped to the cap. */
  tenure: number;
  /** MAS stress-test floor, from GlobalCalculatorSettings (4% for banks). TDSR is
   *  assessed at this rate, never at the contracted one. */
  stressTestRate: number;
  /** The rate actually offered by the bank — what the repayment will really cost. */
  bankRate: number;
  /** True when B still carries another housing loan — drops the LTV cap to 45%. */
  hasOtherHousingLoan: boolean;
}

export interface DecouplingInputs {
  tab: DecouplingPropertyTab;
  property: DecouplingProperty;
  /** The exiting owner, whose share moves and whose name is freed. */
  exiting: DecouplingOwner;
  /** The remaining owner, who acquires the share and ends up with 100%. */
  remaining: DecouplingOwner;
  fees: DecouplingFees;
  financing: DecouplingFinancingInputs;
}

export interface TransferCostResult {
  /** Market value of the share that moves. */
  shareValue: number;
  /** BSD on the share value — paid by the remaining owner. */
  bsd: number;
  /** ABSD on the share value — paid by the remaining owner. 0 when it's their only property. */
  absd: number;
  absdRatePct: number;
  /** SSD on the share value — paid by the exiting owner. */
  ssd: number;
  ssdDetail: SellerStampDutyResult | null;
  /** Fees billed to the remaining owner. */
  remainingOwnerFees: number;
  /** Fees billed to the exiting owner. */
  exitingOwnerFees: number;
  /** Everything the remaining owner pays: BSD + ABSD + their fees. */
  remainingOwnerTotal: number;
  /** Everything the exiting owner pays: SSD + their fees. */
  exitingOwnerTotal: number;
  /** The all-in cost of decoupling, across both parties. */
  totalCost: number;
}

export interface FinancingResult {
  /** The share the remaining owner must buy out. */
  buyoutAmount: number;
  /** The part of the buyout the bank will fund — buyoutAmount at the applicable LTV. */
  buyoutFinanced: number;
  /** The rest of the buyout, which must come from cash or CPF. */
  buyoutDownpayment: number;
  /** How much of that downpayment the buyer's own funds don't cover. */
  downpaymentShortfall: number;
  /** The remaining owner's own slice of the loan being redeemed. */
  ownLoanShare: number;
  /** Cash + CPF the remaining owner puts in. */
  ownFundsApplied: number;
  /** New sole-name loan needed after their own funds. */
  requiredLoan: number;
  /** Ceiling from the LTV rules, against the whole property's market value. */
  ltvCap: number;
  ltvPct: number;
  /** Ceiling from income under TDSR (and MSR where it applies). */
  incomeCap: number;
  /** Assessable monthly income after MAS haircuts. */
  assessableIncome: number;
  /** Monthly repayment capacity that binds — the lower of the TDSR and MSR headroom. */
  bindingMonthlyCapacity: number;
  /** True when MSR is the binding constraint rather than TDSR. */
  msrBinds: boolean;
  /** True when MSR applies at all (HDB, or EC from a developer). */
  msrApplies: boolean;
  /** Longest tenure allowed given the owner's age and the chosen LTV band. */
  tenureCap: number;
  /** Tenure actually used, after clamping. */
  effectiveTenure: number;
  /** The lower of ltvCap and incomeCap — what the bank will actually lend. */
  maxLoan: number;
  /** How far short the financing falls. 0 means it clears. */
  shortfall: number;
  /** True when the remaining owner can carry the property alone. */
  passes: boolean;
  /** Monthly repayment on the required loan at the bank's contracted rate — what the
   *  remaining owner actually pays each month. */
  monthlyAtBankRate: number;
  /** The same repayment at the MAS stress floor, which is what TDSR is tested against. */
  monthlyAtStressRate: number;
  /** Cash the remaining owner must bring on completion: duties, their fees, the
   *  uncovered buyout downpayment, and any loan shortfall. */
  cashRequired: number;
}

export interface ExitingOwnerResult {
  /** Consideration received for the share. */
  consideration: number;
  /** CPF principal + accrued interest returning to their OA. Not cash. */
  cpfRefund: number;
  /** Their slice of the joint loan, redeemed out of the consideration. */
  loanShareRedeemed: number;
  ssd: number;
  fees: number;
  /** What actually reaches their pocket. Can be negative — they'd need to top up. */
  netCash: number;
  /** What they hold across both pockets after the transfer. */
  totalReceived: number;
}

export type GuardLevel = 'blocked' | 'warning' | 'advisory';

export interface DecouplingGuard {
  level: GuardLevel;
  title: string;
  body: string[];
}

export interface EligibilityResult {
  /** True when decoupling is not legally available — the UI disables the inputs. */
  blocked: boolean;
  guards: DecouplingGuard[];
}

// ─── Loan formulas (local copy — see file header) ─────────────────────────────

/** Standard PMT: monthly repayment on `principal` at `annualRate` over `tenureYears`. */
export function calcMonthlyInstallment(
  principal: number,
  annualRate: number,
  tenureYears: number,
): number {
  if (principal <= 0 || tenureYears <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = tenureYears * 12;
  if (r === 0) return principal / n;
  const factor = Math.pow(1 + r, n);
  return principal * (r * factor) / (factor - 1);
}

/** Present value of an annuity: the largest loan `monthlyPayment` can service. */
export function calculateMaxLoan(
  monthlyPayment: number,
  annualRate: number,
  tenureYears: number,
): number {
  if (monthlyPayment <= 0 || tenureYears <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = tenureYears * 12;
  if (r === 0) return Math.round(monthlyPayment * n);
  return Math.round(monthlyPayment * (1 - Math.pow(1 + r, -n)) / r);
}

/**
 * Assessable monthly income after MAS Notice 632 haircuts: base at 100% (70% when
 * self-employed), variable income at 70%. Mirrors calculateAssessableIncome() in
 * haircutFormulas.ts, which takes strings.
 */
export function assessableMonthlyIncome(
  monthlyIncome: number,
  otherAnnualIncome: number,
  isSelfEmployed: boolean,
): number {
  const base = isSelfEmployed ? monthlyIncome * 0.7 : monthlyIncome;
  const variable = (otherAnnualIncome / 12) * 0.7;
  return Math.max(0, base) + Math.max(0, variable);
}

/** Longest tenure for an owner of `age`, for the standard (75% LTV) or extended band. */
export function getTenureCap(age: number, extended: boolean): number {
  const cap = extended ? TENURE_CAP_EXTENDED_YEARS : TENURE_CAP_STANDARD_YEARS;
  const ceiling = extended ? TENURE_AGE_CEILING_EXTENDED : TENURE_AGE_CEILING_STANDARD;
  return Math.max(0, Math.min(cap, ceiling - Math.max(0, age)));
}

/** MAS caps an HDB loan at 25 years, with no extended-tenure regime. */
export const HDB_TENURE_CAP_YEARS = 25;

/**
 * The longest tenure the slider should offer. Private and EC may stretch into the
 * extended band (at the cost of a 55% LTV); HDB is hard-capped at 25 years.
 *
 * Shared by the page and by computeRemainingOwnerFinancing so the two cannot drift.
 */
export function getMaxTenureCap(age: number, tab: DecouplingPropertyTab): number {
  const standard = getTenureCap(age, false);
  return tab === 'hdb'
    ? Math.min(HDB_TENURE_CAP_YEARS, standard)
    : getTenureCap(age, true);
}

// ─── Share value ──────────────────────────────────────────────────────────────

/** Market value of the share being transferred — the basis for BSD, ABSD and SSD. */
export function getShareValue(marketValue: number, sharePct: number): number {
  if (marketValue <= 0 || sharePct <= 0) return 0;
  return Math.round(marketValue * Math.min(100, sharePct) / 100);
}

// ─── Transfer costs ───────────────────────────────────────────────────────────

function sumRemainingOwnerFees(fees: DecouplingFees): number {
  return Math.max(0, fees.buyerLegalFee) + Math.max(0, fees.bankLoanPenalty);
}

function sumExitingOwnerFees(fees: DecouplingFees): number {
  return Math.max(0, fees.sellerLegalFee);
}

/**
 * Duties and fees on the transfer, split by who bears them.
 *
 * BSD and ABSD are charged on the value of the share acquired, so the progressive BSD
 * bands bite on the share value — not the whole property. SSD likewise applies only to
 * the share disposed of.
 */
export function computeTransferCosts(inputs: DecouplingInputs): TransferCostResult {
  const { property, exiting, remaining, fees } = inputs;
  const shareValue = getShareValue(property.marketValue, exiting.sharePct);

  const bsd = calculateBsd(shareValue);

  // The acquirer's ABSD rate. When this property becomes their only one — the usual
  // case, since they already part-own it — an SC pays nothing, which is the whole
  // reason decoupling is cheaper than buying a second property outright.
  const absdRatePct = getAbsdRate(remaining.citizenship, remaining.propertyCount);
  const absd = Math.round(shareValue * absdRatePct / 100);

  const ssdDetail = property.ssdApplies
    ? calculateSellerStampDuty(property.purchaseDate, property.transferDate, shareValue)
    : null;
  const ssd = ssdDetail?.amount ?? 0;

  const remainingOwnerFees = sumRemainingOwnerFees(fees);
  const exitingOwnerFees = sumExitingOwnerFees(fees);

  const remainingOwnerTotal = bsd + absd + remainingOwnerFees;
  const exitingOwnerTotal = ssd + exitingOwnerFees;

  return {
    shareValue,
    bsd,
    absd,
    absdRatePct,
    ssd,
    ssdDetail,
    remainingOwnerFees,
    exitingOwnerFees,
    remainingOwnerTotal,
    exitingOwnerTotal,
    totalCost: remainingOwnerTotal + exitingOwnerTotal,
  };
}

// ─── The remaining owner's sole-name financing ────────────────────────────────

/**
 * Whether the remaining owner can carry the property on their own income.
 *
 * This is where most decoupling plans die: the existing joint loan is discharged in full
 * on completion and replaced by a new facility in one name, so a mortgage two salaries
 * chose must now clear TDSR on one — stressed at the MAS floor, not the contracted rate.
 *
 * The loan needed covers the financed part of the buyout — the other share at the
 * applicable LTV, with the balance falling to the buyer as a downpayment — plus the
 * remaining owner's own slice of the loan being redeemed, less whatever cash and CPF
 * they put in beyond that downpayment.
 */
export function computeRemainingOwnerFinancing(
  inputs: DecouplingInputs,
  costs: TransferCostResult,
): FinancingResult {
  const { property, remaining, financing, tab } = inputs;

  const buyoutAmount = costs.shareValue;
  const ownLoanShare = Math.round(
    Math.max(0, property.outstandingLoan) * Math.min(100, Math.max(0, remaining.sharePct)) / 100,
  );
  const ownFundsApplied = Math.max(0, financing.cashAvailable) + Math.max(0, financing.cpfAvailable);

  // Tenure band. Asking for more than the standard cap is allowed, but drops the LTV.
  const standardCap = getTenureCap(financing.age, false);
  const extendedCap = getTenureCap(financing.age, true);
  const wantsExtended = financing.tenure > standardCap;
  const tenureCap = wantsExtended ? extendedCap : standardCap;
  const effectiveTenure = Math.max(0, Math.min(financing.tenure, tenureCap));
  // A tenure request the extended band can't satisfy either lands back in the standard
  // band, so don't penalise the LTV for a cap the borrower never actually exceeded.
  const usesExtendedBand = wantsExtended && effectiveTenure > standardCap;

  const ltvPct = financing.hasOtherHousingLoan
    ? LTV_SECOND_LOAN
    : usesExtendedBand
      ? LTV_EXTENDED
      : LTV_STANDARD;
  const ltvCap = Math.round(Math.max(0, property.marketValue) * ltvPct);

  // Buying out the other share is an acquisition in its own right, so the bank funds it
  // only up to the LTV — the slice above that is a downpayment the buyer has to find in
  // cash or CPF. Redeeming their own half of the old loan is a refinance, which carries
  // across in full. Own funds are consumed by the downpayment first; only what is left
  // over shrinks the loan.
  const buyoutFinanced = Math.round(buyoutAmount * ltvPct);
  const buyoutDownpayment = buyoutAmount - buyoutFinanced;
  const downpaymentShortfall = Math.max(0, buyoutDownpayment - ownFundsApplied);
  const fundsAfterDownpayment = Math.max(0, ownFundsApplied - buyoutDownpayment);
  const requiredLoan = Math.max(0, buyoutFinanced + ownLoanShare - fundsAfterDownpayment);

  const assessableIncome = assessableMonthlyIncome(
    financing.monthlyIncome,
    financing.otherAnnualIncome,
    financing.isSelfEmployed,
  );

  const availableTdsr = Math.max(
    0,
    assessableIncome * TDSR_RATIO - Math.max(0, financing.existingMonthlyDebt),
  );
  // MSR bites on HDB and on ECs bought from a developer. It is computed on gross income
  // and, unlike TDSR, is not reduced by other debts.
  const msrApplies = tab === 'hdb' || tab === 'ec';
  const availableMsr = msrApplies ? Math.max(0, assessableIncome * MSR_RATIO) : Infinity;
  const bindingMonthlyCapacity = Math.min(availableTdsr, availableMsr);
  const msrBinds = msrApplies && availableMsr < availableTdsr;

  const incomeCap = calculateMaxLoan(
    bindingMonthlyCapacity,
    financing.stressTestRate,
    effectiveTenure,
  );

  const maxLoan = Math.min(ltvCap, incomeCap);
  const shortfall = Math.max(0, requiredLoan - maxLoan);

  const monthlyAtBankRate = calcMonthlyInstallment(
    requiredLoan,
    financing.bankRate,
    effectiveTenure,
  );
  const monthlyAtStressRate = calcMonthlyInstallment(
    requiredLoan,
    financing.stressTestRate,
    effectiveTenure,
  );

  // On completion the remaining owner must produce their duties, their share of the
  // fees, the part of the buyout downpayment their own funds don't cover, and anything
  // the bank won't lend.
  const cashRequired = costs.remainingOwnerTotal + downpaymentShortfall + shortfall;

  return {
    buyoutAmount,
    buyoutFinanced,
    buyoutDownpayment,
    downpaymentShortfall,
    ownLoanShare,
    ownFundsApplied,
    requiredLoan,
    ltvCap,
    ltvPct,
    incomeCap,
    assessableIncome,
    bindingMonthlyCapacity,
    msrBinds,
    msrApplies,
    tenureCap,
    effectiveTenure,
    maxLoan,
    shortfall,
    passes: shortfall === 0 && requiredLoan >= 0,
    monthlyAtBankRate,
    monthlyAtStressRate,
    cashRequired,
  };
}

// ─── The exiting owner's position ─────────────────────────────────────────────

/**
 * What the exiting owner walks away with.
 *
 * Their CPF refund — principal plus accrued interest — goes back to their own OA and
 * cannot be taken as cash, though it is available again for their next purchase. Their
 * slice of the joint loan is redeemed out of the consideration.
 *
 * `netCash` can come out negative on a highly geared property: the loan share and CPF
 * refund together can exceed the share value, leaving the owner to top up on completion.
 * That is a real outcome, so it is not clamped.
 */
export function computeExitingOwnerPosition(
  inputs: DecouplingInputs,
  costs: TransferCostResult,
): ExitingOwnerResult {
  const { property, exiting } = inputs;

  const consideration = costs.shareValue;
  const cpfRefund = Math.max(0, exiting.cpfWithdrawal);
  const loanShareRedeemed = Math.round(
    Math.max(0, property.outstandingLoan) * Math.min(100, Math.max(0, exiting.sharePct)) / 100,
  );

  const netCash = consideration - cpfRefund - loanShareRedeemed - costs.exitingOwnerTotal;

  return {
    consideration,
    cpfRefund,
    loanShareRedeemed,
    ssd: costs.ssd,
    fees: costs.exitingOwnerFees,
    netCash: Math.round(netCash),
    totalReceived: Math.round(netCash) + cpfRefund,
  };
}

// ─── Eligibility guards ───────────────────────────────────────────────────────

function yearsBetween(fromIso: string, toIso: string): number | null {
  if (!fromIso || !toIso) return null;
  const from = new Date(`${fromIso}T00:00:00`);
  const to = new Date(`${toIso}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return (to.getTime() - from.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
}

/**
 * Legal and tax-risk guards for the chosen tab, returned as data so the page just
 * renders them. A `blocked` result means the calculator's inputs should be disabled.
 */
export function getDecouplingEligibility(inputs: DecouplingInputs): EligibilityResult {
  const { tab, property, exiting } = inputs;
  const guards: DecouplingGuard[] = [];

  if (tab === 'hdb') {
    return {
      blocked: true,
      guards: [
        {
          level: 'blocked',
          title: 'HDB flats cannot be decoupled',
          body: [
            `Since ${HDB_DECOUPLING_BAN_DATE_LABEL}, HDB has not allowed married couples to transfer ownership between themselves.`,
            'The only exceptions are divorce, the death of an owner, documented financial hardship, loss of citizenship, or a court order — each assessed case by case by HDB.',
            'The usual alternative: sell the flat on the open market and buy the next private property in one name only, keeping the other name free for a later purchase.',
          ],
        },
      ],
    };
  }

  if (tab === 'ec') {
    guards.push({
      level: 'warning',
      title: `An EC can only be decoupled after its ${EC_MOP_YEARS}-year MOP`,
      body: [
        `Until the ${EC_MOP_YEARS}-year Minimum Occupation Period ends, an EC follows HDB rules and no transfer between owners is possible.`,
        'After MOP it is treated as private property and this calculation applies.',
        'Note the SSD clock runs from the original key-collection date, not from the end of MOP — so an EC can clear its MOP and still sit inside the SSD window.',
      ],
    });
  }

  const holdingYears = yearsBetween(property.purchaseDate, property.transferDate);

  if (exiting.sharePct > 0 && exiting.sharePct <= NINETY_NINE_TO_ONE_SHARE_PCT) {
    guards.push({
      level: 'advisory',
      title: `Transferring only ${exiting.sharePct}% — IRAS audits this pattern`,
      body: [
        'IRAS treats 99-to-1 and similar arrangements as tax avoidance under s33A of the Stamp Duties Act where the main aim was to sidestep ABSD on a planned second purchase.',
        'Where it applies, the original ABSD becomes payable plus a 50% surcharge.',
        'A small share is defensible when the split has a documented commercial reason — a non-working spouse, estate planning, or rental-income allocation — and is not timed around the next purchase.',
      ],
    });
  }

  if (holdingYears !== null && holdingYears < 4) {
    guards.push({
      level: 'warning',
      title: 'Still inside the SSD window',
      body: [
        'The transfer is taxed like an early sale, so SSD applies to the share that moves.',
        'On a recently bought property this alone can outweigh the ABSD the decoupling was meant to save.',
      ],
    });
  }

  return { blocked: false, guards };
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

/**
 * Conveyancing is the one cost that barely moves, so both sides are seeded at the going
 * rate — the same $3,000 the Selling Calculator pre-fills. The loan penalty depends on
 * the lender and on whether a lock-in is still running, so it starts empty.
 */
export const DEFAULT_DECOUPLING_FEES: DecouplingFees = {
  buyerLegalFee: 3_000,
  sellerLegalFee: 3_000,
  bankLoanPenalty: 0,
};

/** Typical 2026 ranges, shown under each field. */
export const FEE_GUIDANCE: Record<keyof DecouplingFees, string> = {
  buyerLegalFee: 'Typically around $3,000',
  sellerLegalFee: 'Typically around $3,000 - the two sides must use different firms',
  bankLoanPenalty: 'Early-redemption penalty if the loan is still within its lock-in - usually ~1.5% of the outstanding amount',
};
